import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { importPaperReservations } from "../api/paperReservations";

function normalizeHeaderKey(x) {
  return String(x || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/\s+/g, " ");
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function toInt(value) {
  const n = toNumber(value);
  if (n === null) return null;
  return Number.isInteger(n) ? n : Math.trunc(n);
}

function toStr(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

const COLUMN_LABELS = {
  lot_no: "Parti No",
  qty: "Miktar",
  customer_name: "Müşteri Adı",
  order_no: "Sipariş No",
  line_no: "Sipariş Kalemi",
};

function validateRow(r) {
  const errors = [];
  if (!toStr(r.lot_no)) errors.push("Parti No boş");
  if (!Number.isFinite(Number(r.qty)) || Number(r.qty) <= 0) errors.push("Miktar geçersiz");
  if (!toStr(r.order_no)) errors.push("Sipariş No boş");
  if (!Number.isFinite(Number(r.line_no)) || Number(r.line_no) <= 0) errors.push("Sipariş Kalemi geçersiz");
  return errors;
}

export default function ReservationsImportModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const validated = useMemo(() => {
    const withErrors = (rows || []).map((r) => ({ ...r, _errors: validateRow(r) }));
    const errorCount = withErrors.reduce((acc, r) => acc + (r._errors?.length || 0), 0);
    const hasBlocking = withErrors.some((r) => (r._errors?.length || 0) > 0);
    return { rows: withErrors, errorCount, hasBlocking };
  }, [rows]);

  async function handlePickFile(file) {
    setParseError("");
    setSubmitError("");
    setResult(null);
    setRows([]);
    setFileName(file?.name || "");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("Excel sayfası bulunamadı");
      const ws = wb.Sheets[sheetName];

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!Array.isArray(aoa) || aoa.length < 2) throw new Error("Excel boş veya başlık satırı eksik");

      const headerRow = aoa[0] || [];
      const headers = headerRow.map((h) => normalizeHeaderKey(h));
      const findIdx = (label) => headers.indexOf(normalizeHeaderKey(label));

      const colIdx = {};
      for (const [key, label] of Object.entries(COLUMN_LABELS)) {
        colIdx[key] = findIdx(label);
      }

      const missing = Object.entries(COLUMN_LABELS)
        .filter(([key]) => colIdx[key] < 0)
        .map(([, label]) => label);
      if (missing.length) throw new Error(`Eksik sütunlar: ${missing.join(", ")}`);

      const parsed = [];
      for (let i = 1; i < aoa.length; i++) {
        const r = aoa[i] || [];
        const lotNo = toStr(r[colIdx.lot_no]);
        if (!lotNo) continue;

        parsed.push({
          lot_no: lotNo,
          qty: toNumber(r[colIdx.qty]),
          customer_name: toStr(r[colIdx.customer_name]),
          order_no: toStr(r[colIdx.order_no]),
          line_no: toInt(r[colIdx.line_no]),
        });
      }

      setRows(parsed);
      if (!parsed.length) setParseError("Dosyada satır bulunamadı (başlık hariç).");
    } catch (err) {
      console.error("EXCEL PARSE ERROR", err);
      setParseError(err?.message || "Excel okunamadı");
    }
  }

  async function submit() {
    setSubmitError("");
    setResult(null);
    if (!rows.length) return;
    if (validated.hasBlocking) {
      setSubmitError("Hatalı satırlar var. Lütfen düzeltin.");
      return;
    }

    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        lot_no: r.lot_no,
        qty: Number(r.qty),
        order_no: r.order_no,
        line_no: Number(r.line_no),
      }));
      const res = await importPaperReservations(payload);
      setResult(res);
      await onImported?.(res);
    } catch (err) {
      console.error("RESERVATIONS IMPORT ERROR", err);
      const status = err?.response?.status;
      const message = err?.response?.data?.error?.message || err?.message || null;
      setSubmitError(`İçe aktarılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Rezervasyon — Excel İçe Aktar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sütunlar: <b>Parti No</b>, <b>Miktar</b>, <b>Müşteri Adı</b>, <b>Sipariş No</b>, <b>Sipariş Kalemi</b>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {parseError ? <Alert severity="error">{parseError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}

          {result ? (
            <Alert severity={result?.failed ? "warning" : "success"}>
              Oluşturulan: <b>{result?.created || 0}</b>, başarısız: <b>{result?.failed || 0}</b>
              {Array.isArray(result?.failures) && result.failures.length ? (
                <ul style={{ margin: "4px 0 0 16px" }}>
                  {result.failures.map((f, i) => (
                    <li key={i}>
                      <b>Satır {f.row_index + 1}{f.lot_no ? ` (${f.lot_no})` : ""}</b>: {f.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" component="label" disabled={saving}>
              Dosya Seç (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `Seçili: ${fileName}` : "Henüz dosya seçilmedi"}
            </Typography>
          </Stack>

          {validated.rows.length ? (
            <Box sx={{ overflowX: "auto" }}>
              <Typography variant="body2" color={validated.hasBlocking ? "error" : "text.secondary"}>
                Satır: <b>{validated.rows.length}</b> — Hata: <b>{validated.errorCount}</b>
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Parti No</TableCell>
                    <TableCell>Miktar</TableCell>
                    <TableCell>Müşteri</TableCell>
                    <TableCell>Sipariş No</TableCell>
                    <TableCell>Kalem</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validated.rows.map((r, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{r.lot_no}</TableCell>
                      <TableCell>{r.qty ?? ""}</TableCell>
                      <TableCell>{r.customer_name || ""}</TableCell>
                      <TableCell>{r.order_no || ""}</TableCell>
                      <TableCell>{r.line_no ?? ""}</TableCell>
                      <TableCell>
                        {r._errors?.length ? (
                          <Typography variant="caption" color="error">{r._errors.join(", ")}</Typography>
                        ) : (
                          <Typography variant="caption" color="success.main">OK</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={saving || !rows.length || validated.hasBlocking}>
          {saving ? "Oluşturuluyor..." : "Rezervasyonları Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
